import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PdfIcon } from 'assets/images/documents';
import { BlockedDoubleClickLoadingButton } from 'library/components/controls/buttons/BlockedDoubleClickLoadingButton';
import { ControlledCommentBox } from 'library/components/controls/form/ControlledCommentBox';
import { ControlledSelect } from 'library/components/controls/form/ControlledSelect';
import { ControlledSwitch } from 'library/components/controls/form/ControlledSwitch';
import { OptionWithIcon } from 'library/models/Option';

import { PdfPreviewPane } from './PdfPreviewPane';

export type PdfPrintTab = 'dealer' | 'customer';

export interface PdfPrintSwitchConfig<TFieldValues extends FieldValues> {
	name: FieldPath<TFieldValues>;
	label: React.ReactNode;
	helperText?: string;
}

export interface PdfPrintLanguageSelectConfig {
	name: string;
	label: string;
	options: OptionWithIcon[];
}

export interface PdfPrintPreviewConfig {
	isLoadingPreview: boolean;
	previewUrl: string;
	objectName?: string;
	generateButtonLabel: string;
	onGenerateClick: () => void;
}

export interface PdfPrintFormLayoutProps<TFieldValues extends FieldValues> {
	control: Control<TFieldValues>;
	onSubmit: (event: React.SubmitEvent<HTMLFormElement>) => void;
	onChange?: (event: React.ChangeEvent<HTMLFormElement>) => void;
	isPreview: boolean;
	isPending: boolean;
	/** Info text shown at the top of the form. */
	description: string;
	dealerTabLabel: string;
	customerTabLabel: string;
	isDealer: boolean;
	onTabChange: (event: React.SyntheticEvent, newValue: PdfPrintTab) => void;
	/** Heading above the additional information comment box. */
	additionalInfoTitle: string;
	commentBoxLabel: string;
	/** When provided, renders a language select above the switches. */
	languageSelect?: PdfPrintLanguageSelectConfig;
	/** Heading above the list of switches. */
	switchSectionLabel: string;
	switches: PdfPrintSwitchConfig<TFieldValues>[];
	preview: PdfPrintPreviewConfig;
	onClose: () => void;
}

/**
 * Generic form body reused by PDF print dialogs (service protocol, health
 * check, ...). Renders the dealer/customer tabs, the additional-information
 * comment box, an optional language select, the configurable list of switches
 * and the preview pane, plus the cancel/print actions. Concrete features drive
 * the react-hook-form wiring and provide their own switches and preview
 * generation.
 */
export const PdfPrintFormLayout = <TFieldValues extends FieldValues>({
	control,
	onSubmit,
	onChange,
	isPreview,
	isPending,
	description,
	dealerTabLabel,
	customerTabLabel,
	isDealer,
	onTabChange,
	additionalInfoTitle,
	commentBoxLabel,
	languageSelect,
	switchSectionLabel,
	switches,
	preview,
	onClose,
}: PdfPrintFormLayoutProps<TFieldValues>) => {
	const { t } = useTranslation(['commonButton']);

	return (
		<form noValidate onSubmit={onSubmit} onChange={onChange}>
			<DialogContent>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 3,
						width: !isPreview ? 930 : 'unset',
					}}>
					<Box sx={{ flex: { xs: 1, md: '0 1 450px' } }}>
						<Alert severity={'info'}>{description}</Alert>
						<Tabs
							sx={{ width: '100%' }}
							value={isDealer ? 'dealer' : 'customer'}
							onChange={onTabChange}>
							<Tab value={'dealer'} label={dealerTabLabel} />
							<Tab value={'customer'} label={customerTabLabel} />
						</Tabs>
						<Typography variant="body1" sx={{ mt: 2, p: 1 }}>
							{additionalInfoTitle}
						</Typography>
						<ControlledCommentBox
							id="additionalText"
							name={'additionalText' as FieldPath<TFieldValues>}
							sx={{ mt: 2, width: '100%' }}
							label={commentBoxLabel}
							control={control}
							size="small"
						/>
						{languageSelect && (
							<ControlledSelect
								id="pdf-print-language-select"
								name={
									languageSelect.name as FieldPath<TFieldValues>
								}
								label={languageSelect.label}
								control={control}
								options={languageSelect.options}
								size="small"
								sx={{ mt: 2, width: '100%' }}
							/>
						)}

						<Stack direction="column">
							<Typography variant="body1" sx={{ my: 2 }}>
								{switchSectionLabel}
							</Typography>
							{switches.map((item) => (
								<ControlledSwitch
									key={item.name}
									control={control}
									name={item.name}
									label={item.label}
									helperText={item.helperText}
									color="secondary"
								/>
							))}
						</Stack>
					</Box>
					<PdfPreviewPane
						isPreview={isPreview}
						isLoadingPreview={preview.isLoadingPreview}
						previewUrl={preview.previewUrl}
						objectName={preview.objectName}
						generateButtonLabel={preview.generateButtonLabel}
						onGenerateClick={preview.onGenerateClick}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between' }}>
				<Button
					variant="outlined"
					color="secondary"
					disableElevation
					onClick={onClose}>
					{t('commonButton:cancel')}
				</Button>
				<BlockedDoubleClickLoadingButton
					variant="contained"
					color="secondary"
					type="submit"
					data-testid={'printForm-isDealer-' + isDealer}
					disableElevation
					loading={isPending}
					endIcon={<PdfIcon />}>
					{t('commonButton:print')}
				</BlockedDoubleClickLoadingButton>
			</DialogActions>
		</form>
	);
};
